import React, { useEffect, useState } from "react";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Ripples from 'react-ripples';
import { decryptToken } from "../utils/getToken";

const RecentActivityTabs = () => {
    const [activeTab, setActiveTab] = useState("invoice");
    const [recentInvoices, setRecentInvoices] = useState([]);
    const [recentExpenses, setRecentExpenses] = useState([]);
    const url = process.env.REACT_APP_LINK;
    const user = getDecryptedUserFromCookie("gta_mainstateuser");
    const navigate = useNavigate();
    const token = decryptToken("accessToken") || ""

    useEffect(() => {
        if (!user?.main_id) return;

        const fetchData = async () => {
            try {
                const [invoiceRes, expenseRes] = await Promise.all([
                    fetch(`${url}/newinvoice/all/${user.main_id}`, {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        }
                    }),
                    fetch(`${url}/api/expenses/all/${user.main_id}`, {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        }
                    })
                ]);

                const [invoiceData, expenseData] = await Promise.all([
                    invoiceRes.json(),
                    expenseRes.json()
                ]);

                const invoices = Array.isArray(invoiceData)
                    ? invoiceData
                        .filter(inv => inv.dpstatus !== "Draft")
                        .sort((a, b) => new Date(b.invoice_date) - new Date(a.invoice_date))
                        .slice(0, 4)
                    : [];

                const expenses = Array.isArray(expenseData)
                    ? expenseData
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .slice(0, 4)
                    : [];

                setRecentInvoices(invoices);
                setRecentExpenses(expenses);
            } catch (err) {
                console.error("Failed to fetch recent data:", err);
            }
        };

        fetchData();
    }, []);

    const renderItem = (item) => (
        <li
            key={item.id || item.invoice_number}
            className="p-4 bg-white rounded-lg shadow flex flex-col gap-1 hover:bg-gray-50 transition"
        >
            <div className="flex justify-between text-sm font-medium text-gray-700">
                <span>{item.customer || item.customer_name || "Unknown"}</span>
                <span className="text-blue-600 font-semibold">
                    ₹ {(item.amount || item.grand_total || 0).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                    })}
                </span>
            </div>
            <div className="flex justify-between text-xs text-gray-500">
                <span>Invoice: {item.invoiceNumber || item.invoice_number || "N/A"}</span>
                <span>{item.date || item.invoice_date || "N/A"}</span>
            </div>
        </li>
    );

    const handleNavigate = () => {
        navigate(activeTab === "invoice" ? "/invoice" : "/expense");
    };

    return (
        <div className="my-8 rounded-lg shadow-md p-4">
            <h4 className="text-[18px] font-semibold text-gray-800 mb-2">Recent Transactions</h4>

            {/* Tab Buttons */}
            <div className="flex space-x-2 mb-4">
                <Ripples>
                    <button
                        className={`px-4 py-2 rounded-md text-sm font-medium ${activeTab === "invoice"
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 text-gray-700"
                            }`}
                        onClick={() => setActiveTab("invoice")}
                    >
                        Invoices
                    </button>
                </Ripples>
                <Ripples>
                    <button
                        className={`px-4 py-2 rounded-md text-sm font-medium ${activeTab === "expense"
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 text-gray-700"
                            }`}
                        onClick={() => setActiveTab("expense")}
                    >
                        Expenses
                    </button>
                </Ripples>
            </div>

            {/* Content with Framer Motion */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                >
                    {activeTab === "invoice" ? (
                        recentInvoices.length === 0 ? (
                            <p className="text-gray-500 text-sm">No recent invoices.</p>
                        ) : (
                            <ul className="space-y-3">{recentInvoices.map(renderItem)}</ul>
                        )
                    ) : recentExpenses.length === 0 ? (
                        <p className="text-gray-500 text-sm">No recent expenses.</p>
                    ) : (
                        <ul className="space-y-3">{recentExpenses.map(renderItem)}</ul>
                    )}
                </motion.div>
            </AnimatePresence>

            {/* View All Button */}
            {(recentInvoices.length === 4 && activeTab === "invoice") ||
                (recentExpenses.length === 4 && activeTab === "expense") ? (
                <div className="text-center mt-4">
                    <Ripples>
                        <button
                            onClick={handleNavigate}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 transition"
                        >
                            View All {activeTab === "invoice" ? "Invoices" : "Expenses"}
                        </button>
                    </Ripples>
                </div>
            ) : null}
        </div>
    );
};

export default RecentActivityTabs;
